<template>
  <section class="tw-py-24 tw-bg-white">
    <div class="tw-container tw-mx-auto tw-px-6">
      <div class="tw-text-center tw-mb-16">
        <h2 class="tw-text-4xl md:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
          Intelligent B2B Matchmaking Built for Your Event
        </h2>
      </div>

      <div class="tw-max-w-6xl tw-mx-auto">
         <!-- Top Image -->
         <div class="tw-mb-8">
           <div class="tw-rounded-2xl tw-shadow-lg tw-overflow-hidden tw-bg-gray-50">
             <img 
               src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&h=600&fit=crop&q=90" 
               alt="Matchmaking Screen Interface"
               class="tw-w-full tw-h-80 tw-object-cover"
               loading="lazy"
             />
           </div>
         </div>

        <!-- Benefits Grid -->
        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-6 tw-mb-8">
          <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="tw-text-2xl tw-font-bold tw-text-primary-600 tw-mb-1">3X</div>
            <div class="tw-text-sm tw-text-gray-600">More meetings</div>
          </div>

          <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="tw-text-2xl tw-font-bold tw-text-primary-600 tw-mb-1">40%</div>
            <div class="tw-text-sm tw-text-gray-600">Higher satisfaction</div>
          </div>

          <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">White-label</div>
            <div class="tw-text-sm tw-text-gray-600">Customization</div>
          </div>

          <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="tw-text-2xl tw-font-bold tw-text-primary-600 tw-mb-1">1</div>
            <div class="tw-text-sm tw-text-gray-600">Week setup</div>
          </div>

          <div class="tw-bg-gray-50 tw-rounded-xl tw-p-6 tw-text-center tw-border tw-border-gray-100">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div class="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-1">Insights</div>
            <div class="tw-text-sm tw-text-gray-600">Reports</div>
          </div>
        </div>

        <!-- Bottom Image and CTA -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-items-center">
           <div class="tw-rounded-xl tw-shadow-lg tw-overflow-hidden tw-bg-gray-50">
             <img 
               src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=500&fit=crop&q=90" 
               alt="Analytics Dashboard"
               class="tw-w-full tw-h-64 tw-object-cover"
               loading="lazy"
             />
           </div>
          <div class="tw-text-center lg:tw-text-left">
            <h3 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-4">
              Ready to transform your event?
            </h3>
            <p class="tw-text-gray-600 tw-mb-6">
              Experience the power of intelligent B2B matchmaking and see results that matter.
            </p>
            <a
              :href="appConfig.calendlyUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="tw-inline-block tw-bg-primary-600 tw-text-white tw-px-8 tw-py-3 tw-rounded-lg tw-text-lg tw-font-semibold hover:tw-bg-primary-700 tw-transition-colors tw-shadow-md hover:tw-shadow-lg"
            >
              Let's Talk About Your Event
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import appConfig from '../config/app.config.js';
</script>
